/**
 * Custom hooks per utilizzare i services
 */

import { useCallback, useRef, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Toast } from 'primereact/toast';
import AuthService from '../services/api/AuthService';
import DocumentService from '../services/api/DocumentService';
import ErrorHandler from '../services/ErrorHandler';
import { AppDispatch } from '../Redux/store';
import { 
  setLoggedIn, 
  setToken, 
  setCodiceFiscale,
  setPatientInfo,
  setEmail,
  resetUser 
} from '../Redux/reducer/userSlice';
import { LoginFormData, PatientInfo, Report } from '../types';

/**
 * Hook per Auth Service
 */
export const useAuth = () => {
  const dispatch = useDispatch<AppDispatch>();

  const login = useCallback(async (credentials: LoginFormData) => {
    console.log('🟢 useAuth.login called with:', credentials.username);
    try {
      const response = await AuthService.login(credentials);
      
      // Aggiorna Redux store - usa lo stesso campo che AuthService ha trovato
      const userToken = response.token || (response as any).accessToken || (response as any).access_token;
      
      // Se non c'è un token nella response, usa il token client da AuthService
      const tokenToUse = userToken || AuthService.getToken();
      console.log('🟢 Setting token in Redux:', tokenToUse ? `Token found (${tokenToUse.length} chars)` : 'No token');
      
      if (tokenToUse) {
        dispatch(setToken(tokenToUse));
      }
      dispatch(setCodiceFiscale(credentials.username));
      dispatch(setLoggedIn(true));
      
      // Il backend ritorna i campi (email, codiceFiscale, userName, ...) a livello root,
      // non dentro response.user. Salviamo qui solo i campi sicuri (email);
      // patientInfo completo viene popolato dalla chiamata successiva a getPatientInfo
      // dentro Login.tsx (che fetcha /user/patient/info per i dati anagrafici).
      const respAny = response as any;
      if (respAny?.email) {
        dispatch(setEmail(respAny.email));
      }
      if (respAny?.user) {
        dispatch(setPatientInfo(respAny.user));
        if (respAny.user.email) dispatch(setEmail(respAny.user.email));
      }

      return response;
    } catch (error) {
      ErrorHandler.handle(error, 'Login');
      throw error;
    }
  }, [dispatch]);

  const logout = useCallback(async () => {
    try {
      await AuthService.logout();
      dispatch(resetUser());
    } catch (error) {
      ErrorHandler.handle(error, 'Logout');
    }
  }, [dispatch]);

  const getPatientInfo = useCallback(async (codiceFiscale: string): Promise<PatientInfo | null> => {
    try {
      const info = await AuthService.getPatientInfo(codiceFiscale);
      dispatch(setPatientInfo(info));
      
      // Se abbiamo l'email nel PatientInfo, salvala anche nello stato
      if (info.email) {
        dispatch(setEmail(info.email));
      }
      
      return info;
    } catch (error) {
      ErrorHandler.handle(error, 'Get Patient Info');
      return null;
    }
  }, [dispatch]);

  const changePassword = useCallback(async (
    username: string,
    oldPassword: string,
    newPassword: string
  ): Promise<boolean> => {
    try {
      await AuthService.changePassword(username, oldPassword, newPassword);
      return true;
    } catch (error) {
      ErrorHandler.handle(error, 'Change Password');
      return false;
    }
  }, []);

  const forgotPassword = useCallback(async (username: string): Promise<boolean> => {
    try {
      await AuthService.forgotPassword(username);
      return true;
    } catch (error) {
      ErrorHandler.handle(error, 'Forgot Password');
      return false;
    }
  }, []);

  const updateEmail = useCallback(async (codiceFiscale: string, newEmail: string): Promise<boolean> => {
    try {
      await AuthService.updateEmail(codiceFiscale, newEmail);
      return true;
    } catch (error) {
      ErrorHandler.handle(error, 'Update Email');
      return false;
    }
  }, []);

  return {
    login,
    logout,
    getPatientInfo,
    changePassword,
    forgotPassword,
    updateEmail,
    isAuthenticated: AuthService.isAuthenticated()
  };
};

/**
 * Hook per Document Service
 */
export const useDocuments = () => {
  const getReports = useCallback(async (username: string): Promise<Report[]> => {
    try {
      return await DocumentService.getReports(username);
    } catch (error) {
      ErrorHandler.handle(error, 'Get Reports');
      return [];
    }
  }, []);

  const downloadReport = useCallback(async (
    episodeId: string, 
    username: string,
    documentId?: string
  ): Promise<Blob | null> => {
    try {
      return await DocumentService.downloadReportAsBlob(episodeId, username, documentId);
    } catch (error) {
      ErrorHandler.handle(error, 'Download Report');
      return null;
    }
  }, []);

  const checkDicomAvailability = useCallback(async (accessionNumber: string) => {
    try {
      return await DocumentService.checkDicomAvailability(accessionNumber);
    } catch (error) {
      ErrorHandler.handle(error, 'Check DICOM');
      return { available: false };
    }
  }, []);

  const startDicomExport = useCallback(async (
    studyUID: string, 
    patientID: string
  ): Promise<string | null> => {
    try {
      return await DocumentService.startDicomExport(studyUID, patientID);
    } catch (error) {
      ErrorHandler.handle(error, 'Start DICOM Export');
      return null;
    }
  }, []);

  const downloadDicomZip = useCallback(async (jobId: string): Promise<Blob | null> => {
    try {
      return await DocumentService.downloadDicomZip(jobId);
    } catch (error) {
      ErrorHandler.handle(error, 'Download DICOM ZIP');
      return null;
    }
  }, []);

  const republishReport = useCallback(async (episodeId: string): Promise<boolean> => {
    try {
      await DocumentService.republishReport(episodeId);
      return true;
    } catch (error) {
      ErrorHandler.handle(error, 'Republish Report');
      return false;
    }
  }, []);

  const clearCache = useCallback(() => {
    DocumentService.clearCache();
  }, []);

  // Alias per compatibilità
  const checkDicomImages = checkDicomAvailability;

  return {
    getReports,
    downloadReport,
    checkDicomAvailability,
    checkDicomImages,  // Alias
    startDicomExport,
    downloadDicomZip,
    republishReport,
    clearCache,
    cacheSize: DocumentService.getCacheSize()
  };
};

/**
 * Hook per Error Handler con Toast
 */
export const useErrorHandler = (toastRef: React.MutableRefObject<Toast | null>) => {
  useEffect(() => {
    ErrorHandler.setToastRef(toastRef);
  }, [toastRef]);

  const handleError = useCallback((error: unknown, context?: string) => {
    ErrorHandler.handle(error, context);
  }, []);

  return { handleError };
};

/**
 * Hook per loading state
 */
export const useLoadingState = <T extends any[], R>(
  asyncFunction: (...args: T) => Promise<R>
) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [data, setData] = useState<R | null>(null);

  const execute = useCallback(async (...args: T) => {
    setLoading(true);
    setError(null);
    try {
      const result = await asyncFunction(...args);
      setData(result);
      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [asyncFunction]);

  return { execute, loading, error, data };
};